import { graphql } from 'gatsby';
import React from 'react';
import type { PageProps } from 'gatsby';
import PhoneDetailsScreen from '../components/screens/PhoneDetailsScreen/PhoneDetailsScreen';
import InactivePhoneScreen from '../components/screens/PhoneDetailsScreen/InactivePhoneScreen/InactivePhoneScreen';
import { StoreLocatorProvider } from '../components/StoreLocator';
import { ContentfulPhoneData } from "../ContentfulTypes";
import { GatsbyPageContextProps } from '../types';
import Layout from '../components/layouts/Layout';

const PhoneDetailsPage = ({
  pageContext,
  location,
  data,
}: PageProps<ContentfulPhoneData, GatsbyPageContextProps>) => (
  <StoreLocatorProvider>
    <Layout locale={pageContext.locale} location={location}>
      {data?.contentfulPhone?.isPhoneActive ? (
        <PhoneDetailsScreen
          data={data}
          location={location}
        />
      ) : (
        <InactivePhoneScreen data={data} />
      )}
    </Layout>
  </StoreLocatorProvider>
);

// ts-prune-ignore-next
export const query = graphql`
  query templatePhoneDetailsPage(
    $formattedLocale: String = "en-CA"
    $urlSlug: String
  ) {
    contentfulPage(
      contentful_id: { eq: "1bEgIlZSZSFODWvWeX6gAf" }
      node_locale: { eq: $formattedLocale }
    ) {
      metaTitle
      metaDescription {
        metaDescription
      }
      contentful_id
      node_locale
      pageSections {
        ... on ContentfulPhoneInventoryThreshold {
          __typename
          id
          threshold
          inventoryStatus {
            inventoryStatusCode
            inventoryStatusIcon {
              asset {
                url
                localImage {
                  id
                  publicURL
                  childImageSharp {
                    gatsbyImageData
                  }
                }
              }
            }
            inventoryStatusMessage {
              inventoryStatusMessage
            }
          }

          storeNotSelectedMessage {
            storeNotSelectedMessage
          }
          carrierNotSelectedMessage {
            carrierNotSelectedMessage
          }
          phonesQuantityLabel
          storeNotSelectedIcon {
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          carrierNotSelectedIcon {
            asset {
              url
              localImage {
                id
                publicURL
                childImageSharp {
                  gatsbyImageData
                }
              }
            }
          }
          selectStoreCta {
              id
              contentful_id
              ctaColor
              ctaTarget
              ctaType
              ctaUrl
              ctaLabel {
                ctaLabel
              }
            }
          changeStoreCta {
            id
            contentful_id
            ctaColor
            ctaTarget
            ctaType
            ctaUrl
            ctaLabel {
              ctaLabel
            }
          }
        }
        ... on ContentfulForm {
          __typename
          contentful_id
          formFields {
            displayLabel
            placeholderLabel
            contentful_id
          }
        }
        ... on ContentfulTextSection {
          __typename
          id
          contentful_id
          heading {
            raw
          }
          contentBody {
            raw
            references {
              ... on ContentfulCta {
                id
                contentful_id
                ctaColor
                ctaTarget
                ctaType
                ctaUrl
                ctaLabel {
                  ctaLabel
                }
              }
            }
          }
        }
        ... on ContentfulCombinatorSection {
          __typename
          contentful_id
          combinatorSectionItems {
            ... on ContentfulTextSection {
              __typename
              id
              contentful_id
              heading {
                raw
              }
              contentBody {
                raw
              }
            }
            ... on ContentfulCta {
              contentful_id
              ctaColor
              ctaLabel {
                ctaLabel
              }
              ctaTarget
              ctaType
              ctaUrl
            }
            ... on ContentfulCardItem {
              __typename
              contentful_id
              cardIcon {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
              cardHeading
              cardDescription {
                cardDescription
              }
              cardCta {
                ctaType
                ctaTarget
                ctaColor
                ctaLabel {
                  ctaLabel
                }
              }
            }
            ... on ContentfulImageSection {
              __typename
              id
              backgroundImageDesktop {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
              imageDescription {
                heading {
                  raw
                }
              }
              backgroundImageMobile {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
            }
            ... on ContentfulBlade {
              __typename
              id
              bladeCloseLabel
              bladeSections {
                ... on ContentfulDamAsset {
                  __typename
                  id
                  altText
                  asset {
                    url
                    localImage {
                      id
                      publicURL
                      childImageSharp {
                        gatsbyImageData
                      }
                    }
                  }
                }
                ... on ContentfulTextSection {
                  __typename
                  id
                  heading {
                    raw
                  }
                  contentBody {
                    raw
                    references {
                      ... on ContentfulCta {
                        id
                        contentful_id
                        ctaColor
                        ctaTarget
                        ctaType
                        ctaUrl
                        ctaLabel {
                          ctaLabel
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    ...AllPhoneBrandsQuery
    contentfulPhoneReservation(
      contentful_id: { eq: "2Q6lonZMIzB4kmYgRXTgNj" }
      node_locale: { eq: $formattedLocale }
    ) {
      cmsName
      contentful_id
      node_locale
      disablePhoneReservation
      closeLabel
      backLabel
      phoneReservationSteps {
        ... on ContentfulForm {
          formFields {
            displayLabel
            contentful_id
          }
          contentful_id
        }
        ... on ContentfulCombinatorSection {
          contentful_id
          combinatorSectionItems {
            ... on ContentfulCardItem {
              __typename
              contentful_id
              cardHeading
              cardIcon {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
              cardDescription {
                cardDescription
              }
            }
            ... on ContentfulForm {
              contentful_id
              formFields {
                displayLabel
                placeholderLabel
                errorMessage
                fieldContent {
                  ... on ContentfulDamAsset {
                    contentful_id
                    asset {
                      id
                      url
                      mimeType
                      localImage {
                        id
                        publicURL
                        childImageSharp {
                          gatsbyImageData
                        }
                      }
                    }
                  }
                }
                contentful_id
                fieldName
              }
            }
            ... on ContentfulTextSection {
              contentful_id
              contentBody {
                raw
              }
              subHeading {
                raw
              }
            }
            ... on ContentfulImageSection {
              contentful_id
              foregroundImageDesktop {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
            }

            ... on ContentfulDppModule {
              contentful_id
              moduleImage {
                altText
                asset {
                  url
                  localImage {
                    id
                    publicURL
                    childImageSharp {
                      gatsbyImageData
                    }
                  }
                }
              }
              moduleHeading
              moduleBody {
                combinatorSectionItems {
                  ... on ContentfulTextSection {
                    contentful_id
                    contentBody {
                      raw
                    }
                  }
                }
              }
              moduleFooter {
                raw
              }
            }

            ...PhobioQuery
          }
        }
      }
      tmsLogo {
        altText
        asset {
          url
          localImage {
            id
            publicURL
            childImageSharp {
              gatsbyImageData
            }
          }
        }
      }
    }
    contentfulPhone(
      urlSlug: { eq: $urlSlug }
      node_locale: { eq: $formattedLocale }
    ) {
      ...ContentfulPhoneQuery
    }
    allContentfulAccessory(
      limit: 14
      filter: {
        node_locale: { eq: $formattedLocale }
        compatiblePhones: { elemMatch: { urlSlug: { eq: $urlSlug } } }
      }
      sort: { fields: unitsSold, order: DESC }
    ) {
      ...AllAccessoriesQuery
    }
  }
`;

export default PhoneDetailsPage;
